import {
  Component,
  DestroyRef,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Signal,
  SimpleChanges,
  TemplateRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormControlName,
  FormGroup,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { BaseComponent } from '@app/components/base/base.component';
import { MenusServices } from '@app/services/menus.service';
import { NotificationService } from '@app/services/notifications.service';
import { ModalService } from '@app/components/modal/modal.service';
import { EntitiesServices } from '@app/services/entities.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { InsuranceService, InsuranceTypeEnum } from '@app/services/insurance.service';
import { finalize, interval, map, tap } from 'rxjs';
import { DealerBranchInsurance, ICommonInsuranceParams, ICommonModelInsurance, QuoteType } from '@app/pages/quote/insurance/insurance-model';
import { insuranceEnum, modelTypeEnum } from '@app/pages/quote/standard-quote/model/standard-quote.model';
import { FinancialCalculatorService } from '@app/pages/quote/open-quote/services/financial-calculator.service';
import { FinancialProductEnum } from '@app/pages/quote/open-quote/model/open-quote.model';
import { TranslateService } from '@ngx-translate/core';
import { createLoadingTracker } from '@app/core/util/loading-tracker';

@Component({
  template: ""
})
export class InsuranceComponentWrapper
  extends BaseComponent
  implements OnInit, ControlValueAccessor, Validator, OnChanges, OnDestroy {
  // SERVICIOS
  private readonly entitiesService = inject(EntitiesServices);
  private readonly fb = inject(FormBuilder);
  public readonly modalService = inject(ModalService);
  protected readonly insuranceService = inject(InsuranceService);
  public readonly financialCalculator = inject(FinancialCalculatorService);
  private readonly translateService = inject(TranslateService);

  // OUTPUTS SIGNALS
  readonly resultCoverage = output<any>();
  readonly resetInsurance = output<void>();
  readonly updateInsurance = output<void>();
  /** Cotización estándar: el usuario aceptó cambiar parámetros de seguro que invalidan cotización/pagos. */
  readonly insuranceSensitiveFieldsConfirm = output<void>();

  // INPUTS SIGNALS
  @Input() idQuote: any;
  @Input() idDeal: any;
  @Input() grace: any[] = [];
  @Input() specialPayments: any[] = [];
  isValid = input<boolean>();
  isValidInsuranceState = true;
  // readonly postalCode = input<string>('');
  readonly quoteType = input<QuoteType>(QuoteType.STANDARD);
  readonly dealerId = input<any>();
  readonly dealerBranchId = input<any>();
  readonly isModelValid = input<boolean>();
  readonly hasManualInsurance = input<boolean>(false);

  // VARIABLES
  minPassenger: number = 20;
  maxPassenger: number = 50;
  minTrailers: number = 1;
  maxTrailers: number = 2;
  formInsurance: FormGroup | null;
  showInsurance = computed(()=> this.insuranceService.showInsurance());
  dealerBranch: DealerBranchInsurance | undefined;
  insuranceSelected: any;
  insuranceEnum = insuranceEnum;
  private readonly loading = createLoadingTracker();
  isLoading = computed(() => this.loading.count() > 0);

  useList: any[] = [];
  typeList: any[] = [];
  readonly postalCodeList = signal<any[]>([]);
  public readonly confirmationData = signal<any>({ title: '', subtitle: '', btn1: '', btn2: '' });

  modelEnum = modelTypeEnum;

  states = signal<any[]>([]);
  form: FormGroup;
  isValidInsurance: boolean = false;

  _postalCode: string;
  _insuranceParams: ICommonInsuranceParams | undefined;
  _insuranceModel: ICommonModelInsurance | undefined;
  _dealerId: any;
  _dealerBranchId: any;
  _modelType: any = "";
  _classification: boolean | null = null;
  _firstTime: boolean = true;
  _insuranceOriginal: any;
  _lastStateName: string = '';
  _focusListeners: (() => void)[] = [];
  _focusElement: HTMLElement | null = null;
  _fieldName: string = '';
  _lastTerm: number | null = null;
  _originalInsuranceValues: any = null;
  modalConfirmation = viewChild<TemplateRef<any>>('modalConfirmation');
  readonly insuranceFlexModal = viewChild.required<TemplateRef<any>>('insuranceModalFlex');
  readonly insuranceSensitiveModalTpl = viewChild<TemplateRef<any>>('insuranceSensitiveModal');
  controlElements: Signal<readonly ElementRef[]> = viewChildren(FormControlName, { read: ElementRef });
  private readonly _watchedInsuranceControlNames: string[] = [
    'stateId',
    'insuranceType',
    'vehicleServiceType',
    'quantityPassengers',
    'loadType',
    'quantityTrailers',
  ];
  /** Último valor confirmado (sincronizado con el padre); se revierte al cancelar el modal. */
  private _insuranceSnapshot: any = null;
  private _pendingInsuranceValue: any = null;
  private _suppressInsuranceFormValueChanges = false;
  searchOrHasBranches = signal(false);

  constructor(
    route: ActivatedRoute,
    menuService: MenusServices,
    notification: NotificationService
  ) {
    super(route, menuService, notification);
    this.createForm();
    effect(() => {
      const currentParams = this.insuranceService.insuranceParams();
      if (this._lastTerm !== currentParams?.term) {
        if (this._lastTerm !== null) {
          const currentType = this.formInsurance?.controls?.['insuranceType']?.value;
          const shouldClear =
            currentType != null &&
            currentParams != null &&
            !this.isInsuranceTypeActiveForParams(currentType, currentParams);
          if (shouldClear) {
            this.formInsurance?.controls?.['insuranceType']?.setValue?.(null, {
              emitEvent: false,
            });
          }
        }
        this._lastTerm = currentParams?.term ?? null;
      }
    }, { allowSignalWrites: true });
  }

  private isInsuranceTypeActiveForParams(type: string, params: ICommonInsuranceParams): boolean {
    const opt = this.insuranceService.insuranceList?.find?.((i) => i.value === type);
    const isActive = opt?.isActive;
    if (typeof isActive !== 'function') return true;
    try {
      return !!isActive(params as any);
    } catch {
      return true;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // if (!this.showInsurance && this.quoteType() == QuoteType.STANDARD) {
    //   return;
    // }
    // Manejar cambios de dealerId/dealerBranchId
    if (this.dealerId() !== this._dealerId || this.dealerBranchId() !== this._dealerBranchId) {
      this.setDealerBranch();
    }
    // Actualizar quoteType y parámetros de seguro
    this.insuranceService.quoteType.set(this.quoteType());
    const currentParams = this.insuranceService.insuranceParams();
    const currentModel = this.insuranceService.insuranceModel();
    if (currentParams) {
      const isAccesoriesProduct = currentModel?.modelType == modelTypeEnum.accesorios;
      const validateChangeClassification = isAccesoriesProduct ? this._classification !== currentModel?.classification : false;
      if (!this._insuranceParams) {
        this._insuranceParams = { ...currentParams };
        this.getStates();
      } else if (this._insuranceParams?.financialProduct !== currentParams?.financialProduct || this._modelType !== currentModel?.modelType || validateChangeClassification) {
        this._insuranceParams.financialProduct = currentParams.financialProduct;
        this._classification = currentModel?.classification ?? null;
        this.getStates();
      }
      if (this._insuranceParams?.term !== currentParams?.term) {
        this._insuranceParams.term = currentParams?.term;
      }
    }

    if (this._modelType !== currentModel?.modelType) {
      this._modelType = currentModel?.modelType;
      this.setUseList();
      this.setValidators();
    }
  }

  ngOnInit() {
    this.insuranceRefresh();
    this.setDealerBranch();
    this.insuranceService.resetForm.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      this.insuranceService.showInsurance.set(false);
      this.formInsurance = null;
      this.resetInsurance.emit();
    })
    this.insuranceService.reloadStatesTrigger.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      this.getStates();
    })
    this.insuranceService.reloadUseListTrigger.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      this.setUseList();
    })
    this.insuranceService.setInsuranceForm.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((value) => {
      if (!value) return;
      this._insuranceOriginal = value;
      const patchForm = {
        id: value?.id,
        insuranceId: value?.insuranceId,
        requestId: value?.requestId,
        insuranceName: value?.insuranceName,
        creationDate: value?.creationDate,
        creationUser: value?.creationUser,
        updateUser: value?.updateUser,
        insuranceType: value?.insuranceType,
        stateId: value?.stateId,
        stateName: value?.stateName,
        packageList: value?.packageList,
        quantityPassengers: value?.quantityPassengers,
        loadType: value?.loadType,
        quantityTrailers: value?.quantityTrailers,
        vehicleServiceType: value?.vehicleServiceType,
        openQuoteInsurancePayments: value?.openQuoteInsurancePayments,
        standardQuoteInsurancePayments: value?.standardQuoteInsurancePayments,
      };
      if (value && !this.insuranceService.showInsurance() && this._firstTime) {
        this.insuranceService.showInsurance.set(true);
        this._firstTime = false;
        if (!this.formInsurance) {
          this.createForm();
        }
        this.getStates(() => {
          this.setUseList();
          this.formInsurance?.patchValue?.(patchForm);
          const state = this.states().find((i) => i.stateId == patchForm.stateId);
          if (state) {
            this.formInsurance?.controls['stateId'].setValue(Number(state.stateId));
          }
          this.captureInsuranceSnapshotFromForm();
        });
      } else if (value && this.formInsurance) {
        this.insuranceService.showInsurance.set(true);
        this.formInsurance?.patchValue?.(patchForm);
        this.captureInsuranceSnapshotFromForm();
      }
    });

    // interval(30000).pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
    //   console.log("Validando FORM INSURANCE");
    //   console.log("Validacion del valor isValid: ", { formInsurance: this.formInsurance?.getRawValue?.(), isValid: this.isValid(), isModelValid: this.isModelValid(), isValidInsuranceState: this.isValidInsuranceState, hasManualInsurance: this.hasManualInsurance() });
    //   for (const key in this.formInsurance?.controls) {
    //     const control = this.formInsurance?.controls?.[key];
    //     if (control?.invalid) {
    //       console.log(`El campo ${key} tiene el siguiente error:`, control?.errors);
    //     }
    //   }
    //   console.log("FIN validacion")
    // });
    this.checkValidity();
  }

  ngOnDestroy(): void {
    this.formInsurance?.reset();
    this._focusListeners.forEach((unregisterFn: any) => unregisterFn());
    this._focusListeners = [];
    this.insuranceService.resetInsuranceForm();
  }


  closeConfirmationModal() {
    this.modalService.closeModal();
  }

  editInsurance() {
    this.modalService.closeModal();
    // if(this.isEditInsurance){
    //   this.zone.runOutsideAngular(() => {
    //     this.restoreFocus();
    //   });
    // }
  }

  setDealerBranch() {
    if (this.searchOrHasBranches()) return;
    const currentDealerId = this.dealerId();
    const currentBranchId = this.dealerBranchId();
    this.searchOrHasBranches.set(true);
    this.loading.track(this.entitiesService.listDealers()).pipe(tap(() => {
      this._dealerId = currentDealerId;
      this._dealerBranchId = currentBranchId;
    }), map(response => {
      const dealer = response.data.find((d: { id: number }) => d.id == currentDealerId);
      // if (!dealer?.dealerBranches) this.insuranceRefresh();
      return dealer && dealer.dealerBranches
        ? dealer.dealerBranches.find((branch: { id: number }) => branch.id == currentBranchId)
        : undefined;
    })).subscribe({
      next: (branch) => {
        this.dealerBranch = branch;
        if (!branch) {
          this.searchOrHasBranches.set(false);
        }
      },
      error: (error) => {
        this.searchOrHasBranches.set(false);
      }
    });
  }

  setUseList() {
    if (this.insuranceService.insuranceModel()?.modelType == modelTypeEnum.autobuses) {
      this.useList = this.insuranceService.useListBuses;
      this.formInsurance?.controls['vehicleServiceType'].setValue(this.insuranceService.useListBuses[0].value);
    } else if (
      this.insuranceService.insuranceModel()?.modelType == modelTypeEnum.tractos ||
      this.insuranceService.insuranceModel()?.modelType == modelTypeEnum.camiones
    ) {
      this.useList = this.insuranceService.useListTracts;
      this.formInsurance?.controls['vehicleServiceType'].setValue(this.insuranceService.useListTracts[0].value);
    }
  }

  setValidators() {
    if (!this.formInsurance || !this.showInsurance()) return;
    if (this._modelType == modelTypeEnum.autobuses) {
      this.formInsurance?.controls['quantityPassengers'].setValidators(
        Validators.required
      );
      // Aca no van ni trailer ni loadType
      this.formInsurance?.controls['loadType'].setValue(null);
      this.formInsurance?.controls['loadType'].setValidators(null);
      this.formInsurance?.controls['quantityTrailers'].setValue(null);
      this.formInsurance?.controls['quantityTrailers'].setValidators(null);
      // this.formInsurance?.controls['postalCode'].setValue(null);
    } else if (this._modelType == modelTypeEnum.tractos) {
      this.formInsurance?.controls['loadType'].setValidators(
        Validators.required
      );
      // this.formInsurance?.controls['quantityTrailers'].setValidators(
      //   Validators.required
      // );
      this.formInsurance?.controls['vehicleServiceType'].setValue("LOAD");
      this.formInsurance?.controls['loadType'].setValue("A");
      this.formInsurance?.controls['quantityTrailers'].setValue(null);
      // Aca no van ni passengers ni loadType
      this.formInsurance?.controls['quantityPassengers'].setValue(null);
      this.formInsurance?.controls['quantityPassengers'].setValidators(null);
    } else if (this._modelType == modelTypeEnum.camiones) {
      this.formInsurance?.controls['quantityTrailers'].setValidators(
        Validators.required
      );
      this.formInsurance?.controls['loadType'].setValidators(
        Validators.required
      );

      this.formInsurance?.controls['vehicleServiceType'].setValue("LOAD");
      this.formInsurance?.controls['loadType'].setValue("A");
      this.formInsurance?.controls['quantityTrailers'].setValue(1);
      // Aca no van ni passengers
      // this.formInsurance?.controls['loadType'].setValue(null);
      // this.formInsurance?.controls['loadType'].setValidators(null);
      this.formInsurance?.controls['quantityPassengers'].setValue(null);
      this.formInsurance?.controls['quantityPassengers'].setValidators(null);
    } else if (this._modelType == modelTypeEnum.accesorios) {
      this.formInsurance?.controls['quantityPassengers'].setValue(null);
      this.formInsurance?.controls['quantityPassengers'].setValidators(null);

      this.formInsurance?.controls['vehicleServiceType'].setValue("LOAD");
      this.formInsurance?.controls['vehicleServiceType'].setValidators(null);

      this.formInsurance?.controls['quantityTrailers'].setValue(1);
      this.formInsurance?.controls['quantityTrailers'].setValidators(null);

      this.formInsurance?.controls['loadType'].setValue("A");
      this.formInsurance?.controls['loadType'].setValidators(null);
    }
  }

  get isEditInsurance() {
    const controls = this.formInsurance?.controls;
    if (!controls) return false;
    return controls["id"]?.value !== null && controls["id"]?.value !== undefined && controls["packageList"]?.value !== null && controls["packageList"]?.value !== undefined;
  }

  createForm() {
    this.formInsurance = this.fb.group({
      id: [, []],
      insuranceId: [, []],
      requestId: [, [Validators.required]],
      insuranceName: [, []],
      creationDate: [, []],
      updatedDate: [, []],
      creationUser: [, []],
      updateUser: [, []],
      insuranceType: [, [Validators.required]],
      stateId: [, [Validators.required]],
      stateName: [, [Validators.required]],
      vehicleServiceType: [, [Validators.required]],
      // postalCode: [, []],
      quantityPassengers: [, []],
      loadType: ['A', []],
      quantityTrailers: [1, []],
      packageList: [, [Validators.required]],
      financialProductType: [FinancialProductEnum.directCredit, []],
      insuranceTerm: [, []],
      openQuoteInsurancePayments: [, []],
      standardQuoteInsurancePayments: [, []],

      annualPercentageRate: [, []],
      downpaymentAmount: [, []],
      openingfeeAmount: [, []],
      balloonAmount: [, []],
      guaranteeDepositAmount: [, []],
      insuranceAmount: [, []],
      additionalServicesAmount: [, []],
      confirmationExpensesAmount: [, []],
      initialPayment: [, []],
      monthlyPayment: [, []],
      annualTotalCost: [, []],
      financialAmount: [, []],
    });

    this.formInsurance.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this.handleInsuranceFormValueChange();
      });

    this.setValidators();
    this.captureInsuranceSnapshotFromForm();
  }

  checkValidity() {
    const baseValidation =
      this.formInsurance?.controls['insuranceType'].valid &&
      this.formInsurance?.controls['stateId'].valid &&
      this.formInsurance?.controls['stateName'].valid &&
      this.formInsurance?.controls['vehicleServiceType'].valid;

    if (this.insuranceService.insuranceModel()?.modelType == modelTypeEnum.autobuses) {
      this.isValidInsurance = Boolean(baseValidation) && Boolean(this.formInsurance?.controls['quantityPassengers'].value);
    } else {
      this.isValidInsurance = Boolean(baseValidation);
    }
  }

  private handleInsuranceFormValueChange(): void {
    if (!this.formInsurance) return;
    if (this._suppressInsuranceFormValueChanges) return;

    const watchedDirty = this._watchedInsuranceControlNames.find((name) =>
      this.formInsurance!.get(name)?.dirty
    );

    const insuranceTypeDirty =
      this.formInsurance.get('insuranceType')?.dirty === true;

    if (
      this.quoteType() === QuoteType.STANDARD &&
      insuranceTypeDirty &&
      this.requiresInsuranceSensitiveConfirmation() &&
      this.shouldConfirmInsuranceTypeChange()
    ) {
      this._pendingInsuranceValue = this.formInsurance.getRawValue();
      this.formInsurance.patchValue(this._insuranceSnapshot ?? {}, { emitEvent: false });
      this.markWatchedInsuranceControlsPristine();
      this.runWithoutInsuranceFormValueChanges(() =>
        this.syncStateNameFromStateIdIfNeeded()
      );
      const modalTpl = this.insuranceSensitiveModalTpl();
      if (modalTpl) {
        this.modalService.component = modalTpl;
        this.modalService.openModal();
      } else {
        this.propagateInsuranceFormChange();
      }
      return;
    }

    if (watchedDirty) {
      this.syncStateNameFromStateIdIfNeeded();
      if (insuranceTypeDirty) {
        this.resetInsuranceCalculated();
      }
      this.markWatchedInsuranceControlsPristine();
    }

    this.propagateInsuranceFormChange();
  }

  /** Misma idea que el modal del producto financiero: deal con datos que se invalidan al cambiar parámetros de seguro. */
  private requiresInsuranceSensitiveConfirmation(): boolean {
    if (!this.idDeal) return false;
    const gr = this.grace ?? [];
    const sp = this.specialPayments ?? [];
    const raw = this.formInsurance?.getRawValue();
    const hasStdPayments =
      Array.isArray(raw?.standardQuoteInsurancePayments) &&
      raw.standardQuoteInsurancePayments.length > 0;
    const hasMarshQuote = !!raw?.id && !!raw?.packageList;
    const hasMarshStarted = !!raw?.id;
    return (
      (Array.isArray(gr) && gr.length > 0) ||
      (Array.isArray(sp) && sp.length > 0) ||
      hasStdPayments ||
      hasMarshQuote ||
      hasMarshStarted
    );
  }

  /** Modal solo al cambiar tipo de seguro, y solo si interviene multianual o anual (Marsh). */
  private shouldConfirmInsuranceTypeChange(): boolean {
    const prev = this._insuranceSnapshot?.insuranceType as string | undefined;
    const next = this.formInsurance?.get('insuranceType')?.value as
      | string
      | undefined;
    return (
      this.isMarshAnnualOrMultiannualType(prev) ||
      this.isMarshAnnualOrMultiannualType(next)
    );
  }

  private isMarshAnnualOrMultiannualType(type: string | undefined | null): boolean {
    if (!type) return false;
    return (
      type === InsuranceTypeEnum.COUNTED_MULTIYEAR ||
      type === InsuranceTypeEnum.FINANCED_MULTIYEAR ||
      type === InsuranceTypeEnum.COUNTED_ANNUAL ||
      type === InsuranceTypeEnum.FINANCED_ANNUAL
    );
  }

  private runWithoutInsuranceFormValueChanges(fn: () => void): void {
    this._suppressInsuranceFormValueChanges = true;
    try {
      fn();
    } finally {
      this._suppressInsuranceFormValueChanges = false;
    }
  }

  private markWatchedInsuranceControlsPristine(): void {
    if (!this.formInsurance) return;
    for (const name of this._watchedInsuranceControlNames) {
      this.formInsurance.get(name)?.markAsPristine();
    }
  }

  private syncStateNameFromStateIdIfNeeded(): void {
    if (!this.formInsurance) return;
    const stateId = this.formInsurance.controls['stateId']?.value;
    if (stateId == null || stateId === '') return;
    const state = this.states().find(
      (i) => String(i.stateId) === String(stateId) || i.stateId == stateId
    );
    if (state) {
      this.formInsurance.controls['stateName'].setValue(state.state, {
        emitEvent: false,
      });
    }
  }

  private captureInsuranceSnapshotFromForm(): void {
    if (!this.formInsurance) {
      this._insuranceSnapshot = null;
      return;
    }
    this._insuranceSnapshot = { ...this.formInsurance.getRawValue() };
  }

  private propagateInsuranceFormChange(): void {
    if (!this.formInsurance) return;
    this.onChange(this.formInsurance.getRawValue());
    this.captureInsuranceSnapshotFromForm();
    this.onTouched();
    this.checkValidity();
  }

  cancelInsuranceSensitiveEdit(): void {
    this._pendingInsuranceValue = null;
    this.markWatchedInsuranceControlsPristine();
    this.runWithoutInsuranceFormValueChanges(() =>
      this.syncStateNameFromStateIdIfNeeded()
    );
    this.propagateInsuranceFormChange();
    this.modalService.closeModal();
  }


  confirmInsuranceSensitiveEdit(): void {
    const pending = this._pendingInsuranceValue;
    this._pendingInsuranceValue = null;
    this.modalService.closeModal();
    if (!this.formInsurance) {
      return;
    }
    this.insuranceService.showInsurance.set(true);
    this.runWithoutInsuranceFormValueChanges(() => {
      if (pending) {
        this.formInsurance!.patchValue(pending, { emitEvent: false });
      }
      this.clearInsuranceQuoteData();
      this.syncStateNameFromStateIdIfNeeded();
    });
    this.markWatchedInsuranceControlsPristine();
    this.captureInsuranceSnapshotFromForm();
    this.propagateInsuranceFormChange();
    this.insuranceSensitiveFieldsConfirm.emit();
  }

  valid(): boolean {
    if (!this.formInsurance) return false;

    if (this.insuranceService.insuranceModel()?.modelType == modelTypeEnum.autobuses) {
      return this.formInsurance?.controls['quantityPassengers'].valid;
    } else if (
      this.insuranceService.insuranceModel()?.modelType == modelTypeEnum.tractos ||
      this.insuranceService.insuranceModel()?.modelType == modelTypeEnum.camiones
    ) {
      return (
        this.formInsurance?.controls['quantityTrailers'].valid &&
        this.formInsurance?.controls['loadType'].valid
      );
    }
    return false;
  }

  getStates(callback?: () => void) {
    const body = {
      financialProduct: this.insuranceService.insuranceParams()?.financialProduct,
      classification: this.insuranceService.insuranceModel()?.classification,
      modelType: this.insuranceService.insuranceModel()?.modelType,
      companyName: this.insuranceService.insuranceParams()?.companyName,
    };
    if (!body.financialProduct || !body.modelType || !body.companyName) return;

    this.loading.track(
      this.insuranceService
        .getStatesParametric(body))
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (response) => {
          this.states.set(response.data);
          const state = this.states().find(
            (i) => i.state == this.formInsurance?.controls['stateName'].value
          );
          if (
            state &&
            this.formInsurance?.controls['stateId'].value !== state?.stateId
          ) {
            this.formInsurance?.controls['stateId'].setValue(Number(state?.stateId));
          }
          this.isValidInsuranceState = true;
          if (callback) {
            callback();
          }
          this.checkValidity();
        },
        error: (error) => {
          this.isValidInsuranceState = false;
        },
      });
  }

  deleteInsurance() {
    // const id = this.formInsurance?.controls['id'].value;
    // if (!id) {
    this.resetForm();

  }

  resetForm() {
    this.insuranceService.showInsurance.update(val=> !val);
    this.formInsurance = null;
    this.resetInsurance.emit();
  }

  insuranceRefresh() {
    if (!this.dealerBranch?.dtfsMarshId && !this.dealerBranch?.sofomMarshId) {
      this.deleteInsurance();
      this.insuranceService.showInsurance.set(false);
      return;
    }
    if (this.formInsurance == null) {
      this.createForm();
    }
    this.setValidators();
  }

  addInsurance() {
    const hasDtfsId = this.dealerBranch?.dtfsMarshId;
    const hasSofomId = this.dealerBranch?.sofomMarshId;

    if (!hasDtfsId || !hasSofomId) {
      this.showNotificationError(
        `La sucursal ${this.dealerBranch?.name ?? ''
        } no tiene agencia asignada en aseguradora. No es posible cotizar seguro`
      );
      return;
    }
    this.insuranceService.showInsurance.update(val=> !val);
    if (!this.formInsurance) {
      this.createForm();
    }
    this.setValidators();
  }

  closeFlexModal(selectedValue?: any) {
    this.modalService.closeModal();
    if (selectedValue) {

      this.insuranceSelected = selectedValue;
      this.insuranceSelected.packageList.coverageListModel =
        this.getListCoverage();
      this.formInsurance?.patchValue(this.insuranceSelected);
      this.formInsurance?.controls['packageList'].setValue(
        this.insuranceSelected.packageList
      );
      this.captureInsuranceSnapshotFromForm();
      const insuranceType = this.formInsurance?.controls['insuranceType'].value;
      // AMORTIZACIONES
      this.updateInsurance.emit();
    } else {
      // RE AGREGAR VALORES PERDIDOS / ORIGINALES
      if (this._originalInsuranceValues) {
        this.formInsurance?.controls['insuranceId'].setValue(this._originalInsuranceValues.insuranceId);
        this.formInsurance?.controls['id'].setValue(this._originalInsuranceValues.id);
        this.formInsurance?.controls['requestId'].setValue(this._originalInsuranceValues.requestId);
        this.formInsurance?.controls['creationDate'].setValue(this._originalInsuranceValues.creationDate);
        this.formInsurance?.controls['updatedDate'].setValue(this._originalInsuranceValues.updatedDate);
        this.formInsurance?.controls['creationUser'].setValue(this._originalInsuranceValues.creationUser);
        this.formInsurance?.controls['updateUser'].setValue(this._originalInsuranceValues.updateUser);
        this.formInsurance?.controls['packageList'].setValue(this._originalInsuranceValues.packageList);
      }
    }

    // Limpiar valores originales guardados
    this._originalInsuranceValues = null;
  }

  deleteInsurancePayments(idQuote: number, idDeal: number, idInsurance: number) {
    return this.insuranceService.deleteMarshInsurancePayments(idQuote, idDeal, idInsurance);
  }

  calculateInsuranceAmortizations(idQuote: any, idDeal: any, idInsurance: any) {
    let model: any = this.financialCalculator.getBodyforCalculateInsuranceMarsh(
      this.formInsurance?.value,
      this.insuranceService.insuranceParams()!
    );
    const payments = this.formInsurance?.controls['openQuoteInsurancePayments'].value;
    const hasExistingInsuranceAndDeal = idInsurance && idQuote && idDeal;
    this.loading.track(this.insuranceService.createInsuranceAmortization(model)).pipe(takeUntilDestroyed(this._destroyRef)).subscribe({
      next: (response) => {
        if (response.error) {
          const message = response.needTranslate ? this.translateService.instant(response.message) : response.message;
          this.showNotificationError(message);
          return;
        }

        if (payments && payments?.length > 0 && hasExistingInsuranceAndDeal) {
          this.loading.track(this.insuranceService.deleteMarshInsurancePayments(idQuote, idDeal, idInsurance)).pipe(takeUntilDestroyed(this._destroyRef)).subscribe({
            next: (res) => {
              this.assignPayments(response);
            }
          });
        } else {
          this.assignPayments(response);
        }
      }
    });
  }

  assignPayments(response: any) {
    const { insuranceResult, payments } = this.insuranceService.getMappedAmortization(response);
    Object.entries(insuranceResult).forEach(([key, value]) => {
      this.formInsurance?.controls[key].setValue(value);
    });
    this.formInsurance?.controls['openQuoteInsurancePayments'].setValue(payments);
  }

  openInsuranceFlexModal() {
    if (!this.dealerBranch?.dtfsMarshId || !this.dealerBranch?.sofomMarshId) {
      this.showNotificationError(
        `La sucursal ${this.dealerBranch?.wholeSale.key ?? ''} ${this.dealerBranch?.name ?? ''
        } no tiene agencia asignada en aseguradora. No es posible cotizar seguro`
      );
      return;
    }
    const hasId = !!this.formInsurance?.controls['id'].value
    // Guardar valores originales antes del reset
    if (hasId) {
      this._originalInsuranceValues = {
        insuranceId: this.formInsurance?.controls['insuranceId'].value,
        id: this.formInsurance?.controls['id'].value,
        requestId: this.formInsurance?.controls['requestId'].value,
        creationDate: this.formInsurance?.controls['creationDate'].value,
        updatedDate: this.formInsurance?.controls['updatedDate'].value,
        creationUser: this.formInsurance?.controls['creationUser'].value,
        updateUser: this.formInsurance?.controls['updateUser'].value,
        packageList: this.formInsurance?.controls['packageList'].value
      };

      // Reset de datos que genera la API para evitar problemas de validaciones y guardado de datos
      this.formInsurance?.controls['insuranceId'].setValue(null);
      this.formInsurance?.controls['id'].setValue(null);
      this.formInsurance?.controls['requestId'].setValue(null);
      this.formInsurance?.controls['creationDate'].setValue(null);
      this.formInsurance?.controls['updatedDate'].setValue(null);
      this.formInsurance?.controls['creationUser'].setValue(null);
      this.formInsurance?.controls['updateUser'].setValue(null);
      this.formInsurance?.controls['packageList'].setValue(null);
    }
    // FIN de reset de datos
    this.modalService.component = this.insuranceFlexModal();
    this.modalService.openModal();
  }

  getListCoverage() {
    let coverages: any[] = [];
    this.insuranceSelected.packageList.coverageListModel.map(
      (coverage: any) => {
        const existingCoverage = coverages.find(
          (c: any) => c.coverageId === coverage.coverageId
        );

        if (!existingCoverage) {
          coverages.push(coverage);
        }
      }
    );
    coverages = coverages.map((i: any) => {
      return { ...i, name: this.transformDescription(i.name) };
    });
    return coverages;
  }

  onChangeQuantity() {
    const quantityTrailers =
      this.formInsurance?.controls['quantityTrailers'].value;
    if (quantityTrailers > 2 || quantityTrailers <= 0) {
      this.formInsurance?.controls['quantityTrailers'].setValue(
        this.minTrailers
      );
    }
  }

  /** Limpia cotización Marsh (tabla de coberturas, pagos e importes) sin tocar tipo/estado/uso/datos del vehículo. */
  private clearInsuranceQuoteData(): void {
    if (!this.formInsurance) return;
    this.formInsurance.patchValue(
      {
        insuranceId: null,
        id: null,
        requestId: null,
        insuranceName: null,
        creationDate: null,
        updatedDate: null,
        creationUser: null,
        updateUser: null,
        packageList: null,
        openQuoteInsurancePayments: null,
        standardQuoteInsurancePayments: null,
        insuranceTerm: null,
        annualPercentageRate: null,
        downpaymentAmount: null,
        openingfeeAmount: null,
        balloonAmount: null,
        guaranteeDepositAmount: null,
        insuranceAmount: null,
        additionalServicesAmount: null,
        confirmationExpensesAmount: null,
        initialPayment: null,
        monthlyPayment: null,
        annualTotalCost: null,
        financialAmount: null,
      },
      { emitEvent: false }
    );
  }

  resetInsuranceCalculated() {
    const hasMarshData =
      !!this.formInsurance?.controls['id']?.value ||
      !!this.formInsurance?.controls['packageList']?.value;
    if (hasMarshData) {
      this.clearInsuranceQuoteData();
    }
  }

  /** @deprecated La sincronización pasa por valueChanges; se mantiene por si algún template legacy lo invoca. */
  getZipCode(event: any) {
    const stateId = event?.target?.value;
    if (!stateId) return;
    this.formInsurance?.controls['stateId'].setValue(stateId);
    const row = this.states().find((i) => i.stateId == stateId);
    if (row) {
      this.formInsurance?.controls['stateName'].setValue(row.state);
    }
    this.resetInsuranceCalculated();
  }

  calculatePassenger(event: any) {
    const quantityPassengers =
      this.formInsurance?.controls['quantityPassengers'].value;
    if (quantityPassengers < this.minPassenger) {
      this.formInsurance?.controls['quantityPassengers'].setValue(
        this.minPassenger
      );
    } else if (quantityPassengers > this.maxPassenger) {
      this.formInsurance?.controls['quantityPassengers'].setValue(
        this.maxPassenger
      );
    }
  }

  selectedPostalCode(postalCodeId: any) {
    this._postalCode = postalCodeId;
    this.formInsurance?.controls['postalCode'].setValue(postalCodeId);
    this._postalCode = postalCodeId;
  }

  //#region CVA
  writeValue(value: any): void {
    if (value) {
      // Si no existe el formulario, inicializarlo primero+
      if (!this.formInsurance) {
        this.createForm();
      }

      this.formInsurance?.patchValue(value, { emitEvent: false });
      if (this.formInsurance) {
        this.insuranceService.showInsurance.set(true);
      }
      this.captureInsuranceSnapshotFromForm();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  private onChange = (_: any) => { };
  private onTouched = () => { };

  //#endregion

  //#region Validator
  validate(_control: AbstractControl<any, any>): ValidationErrors | null {
    if (this.formInsurance && this.showInsurance()) {
      return this.formInsurance.valid ? null : { invalidFormFinance: true };
    } else {
      return null;
    }
  }

  //#endregion


  get stateId() {
    return this.formInsurance?.controls['stateId'].value;
  }

  get typeInsuranceParams() {
    return { term: this.insuranceService.insuranceParams()?.term }
  }

  get notIsTruckAndTracts() {
    return this._modelType != modelTypeEnum.tractos && this._modelType != modelTypeEnum.camiones && this._modelType != modelTypeEnum.accesorios;
  }

  get isTruckAndTracts() {
    return this._modelType == modelTypeEnum.tractos || this._modelType == modelTypeEnum.camiones;
  }

  get isTracts() {
    return this._modelType == modelTypeEnum.tractos;
  }
  get hasInsuranceNotCalculated() {
    const form = this.formInsurance?.getRawValue();
    return form !== null && form?.id != null && form?.packageList == null;
  }
}
